//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import ArrayStore from 'devextreme/data/array_store'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { ColumnModel } from "../../modules/api/api.models"
import { Enums } from '../../models/common/enums'
import { OpData, OpDataType, OpDevice } from '../../models/core'
import { SESSION } from '../../models/custom/session'

import { CommonHelper, ConvertHelper, LocalizationHelper } from "../../helpers"
import { DataProvider } from '../../helpers/data-provider'
import { IUserSession } from "../../helpers/user-session"
import { ToastHelper } from '../../helpers/toast-helper'

import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'
import { DetailsOptions } from "../../modules/common/details-popup/details-popup.directive"
import { GridTypes } from "../../modules/common/grid/grid.controller"
import { TreeListOptions } from "../../modules/common/tree-list/tree-list.controller"

// #region Consts

export const DEVICE_SERVICE_NAME: string = 'DeviceService'
export const PROVIDER_NAME: string = 'DeviceServiceProvider'

// #endregion

export class DeviceService {
    // #region Consts

    public static get FILE_NAME_PREFIX(): string { return 'devices' }
    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Device]' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.SerialNbr }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Device }

    public static get TreeKeyFieldName() { return 'idTreeItem' }
    public static get TreeParentFieldName() { return 'idParentTreeItem' }

    // #endregion

    // #region Ctor

    constructor(private rootScope: ng.IRootScopeService) {
    }

    // #endregion

    // #region Public Methods

    public deleteDevice(dataProvider: DataProvider, serialNbr: number): JQueryPromise<void> {
        const deferred: JQueryDeferred<void> = $.Deferred<void>()
        const toDelete: OpDevice = new OpDevice(null)

        toDelete.serialNbr = serialNbr

        dataProvider.Device
            .deleteDevice(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, DeviceService.SIMAX_MODULE_NAME))
                deferred.resolve()
            })
            .fail((error: any) => {
                let errorMsg: string = error

                if (SESSION.isType(error)) {
                    errorMsg = (<SESSION>error).toString()
                    ToastHelper.showError(errorMsg)
                } else {
                    ToastHelper.showError(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, DeviceService.SIMAX_MODULE_NAME))
                }

                deferred.reject(errorMsg)
            })

        return deferred.promise()
    }

    public saveDevice(dataProvider: DataProvider, device: OpDevice, moduleName: Enums.SimaxModuleName, isBulkSave: boolean = false): JQueryPromise<OpDevice> {
        const deferred: JQueryDeferred<OpDevice> = $.Deferred<OpDevice>()

        dataProvider.Device
            .saveDevice(device)
            .then((savedObject: OpDevice) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, moduleName))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.deviceSaved, true, 0)
                }

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    const errorMsg: string = ConvertHelper.getRequestErrorMessage(error)
                    ToastHelper.showError(errorMsg || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                }

                deferred.reject(error)
            })

        return deferred.promise()
    }

    public getDeviceConfigurationInconsistency(dataProvider: DataProvider, serialNbr: number[]): JQueryPromise<OpData[]> {
        return dataProvider.Device.getDeviceConfigurationInconsistency(serialNbr)
    }

    // #endregion

    // #region Options

    public getDetailsOptions(serialNbr: number): DetailsOptions {
        return {
            moduleName: DeviceService.SIMAX_MODULE_NAME,
            referenceObjectId: serialNbr,
        }
    }

    public getCreateEditOptions(userSession: IUserSession,
        referenceObjectId: number = 0,
        referenceObject: OpDevice = null,
        selectedTabType: Enums.SimaxCreateEditSelectedTabType = Enums.SimaxCreateEditSelectedTabType.BaseParameters,
        dataTypeGroupReferenceValue: number = null
    ): CreateEditInitParams {
        return {
            createEditSelectedTabType: selectedTabType,
            groupReferenceType: Enums.ReferenceType.IdDeviceType,
            groupReferenceValue: referenceObject != null ? referenceObject.idDeviceType : dataTypeGroupReferenceValue != null ? dataTypeGroupReferenceValue : null,
            idReferenceType: DeviceService.REFERENCE_TYPE,
            moduleName: DeviceService.SIMAX_MODULE_NAME,
            referenceObject: referenceObject,
            referenceObjectId: referenceObjectId,
            sections: null,
            useBusinessObjects: true,
        }
    }

    public getDeviceHierarchyGridColumns(): ColumnModel[] {
        const columns: ColumnModel[] = []

        const slotNbrColumn: ColumnModel = new ColumnModel()
        slotNbrColumn.idName = 'slotNbr'
        slotNbrColumn.name = 'SlotNbr'
        slotNbrColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SlotNbr)
        slotNbrColumn.type = Enums.DataTypeClass.Text
        columns.push(slotNbrColumn)

        const slotTypeColumn: ColumnModel = new ColumnModel()
        slotTypeColumn.idName = 'slotType'
        slotTypeColumn.name = 'SlotType'
        slotTypeColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SlotType)
        slotTypeColumn.type = Enums.DataTypeClass.Text
        columns.push(slotTypeColumn)

        const protocolInColumn: ColumnModel = new ColumnModel()
        protocolInColumn.idName = 'protocolIn'
        protocolInColumn.name = 'ProtocolIn'
        protocolInColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ProtocolIn)
        protocolInColumn.type = Enums.DataTypeClass.Text
        columns.push(protocolInColumn)

        const protocolOutColumn: ColumnModel = new ColumnModel()
        protocolOutColumn.idName = 'protocolOut'
        protocolOutColumn.name = 'ProtocolOut'
        protocolOutColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ProtocolOut)
        protocolOutColumn.type = Enums.DataTypeClass.Text
        columns.push(protocolOutColumn)

        const activeColumn: ColumnModel = new ColumnModel()
        activeColumn.idName = 'isActive'
        activeColumn.name = 'Active'
        activeColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Active)
        activeColumn.type = Enums.DataTypeClass.Boolean
        columns.push(activeColumn)

        return columns
    }

    public getParentDevicesGridColumns(): ColumnModel[] {
        const columns: ColumnModel[] = this.getDeviceHierarchyGridColumns()

        const deviceColumn: ColumnModel = new ColumnModel()
        deviceColumn.idName = 'deviceParent'
        deviceColumn.name = 'DeviceParent'
        deviceColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device)
        deviceColumn.type = Enums.DataTypeClass.Text
        columns.push(deviceColumn)

        return columns
    }

    public getChildDevicesGridColumns(): ColumnModel[] {
        const columns: ColumnModel[] = this.getDeviceHierarchyGridColumns()

        const deviceColumn: ColumnModel = new ColumnModel()
        deviceColumn.idName = 'device'
        deviceColumn.name = 'Device'
        deviceColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device)
        deviceColumn.type = Enums.DataTypeClass.Text
        columns.push(deviceColumn)

        return columns
    }

    public getExtededParametersGridColumns(): ColumnModel[] {
        const descriptionColumn: ColumnModel = new ColumnModel()
        descriptionColumn.idName = 'dataType'
        descriptionColumn.name = 'DataType'
        descriptionColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)
        descriptionColumn.type = Enums.DataTypeClass.Text

        const indexColumn: ColumnModel = new ColumnModel()
        indexColumn.idName = 'indexNbr'
        indexColumn.name = 'Index'
        indexColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index)
        indexColumn.type = Enums.DataTypeClass.Text

        const valueColumn: ColumnModel = new ColumnModel()
        valueColumn.idName = 'value'
        valueColumn.name = 'Value'
        valueColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value)
        valueColumn.type = Enums.DataTypeClass.Text

        const unitColumn: ColumnModel = new ColumnModel()
        unitColumn.idName = 'unit'
        unitColumn.name = 'Unit'
        unitColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Unit)
        unitColumn.type = Enums.DataTypeClass.Text

        return [descriptionColumn, indexColumn, valueColumn, unitColumn]
    }

    public getExtendedParametersOptions(showLegend: boolean = true): TreeListOptions {
        const options: TreeListOptions = {
            name: 'DevicesDetails',
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            buttonsOptions: {
                enabled: showLegend,
            },

            addCustomFilters: true,
            columnsDictionary: this.getExtededParametersGridColumns(),
            dataSource: null,
            showDatatypeLegend: showLegend,
            localStorageVarForSavedEntity: 'deviceSavedParamGrid',

            keyExpr: DeviceService.TreeKeyFieldName,
            parentIdExpr: DeviceService.TreeParentFieldName
        }

        return options
    }

    /**
     * This method returns device configuration inconsistency options. The options form may be for legacy controls or refactored controls. The result is
     * based on the `useLegacy` parameter. If you need new options, you have to specificy `useLegacy` parameter explicitly with value `false`.
     * @param dataProvider
     * @param dataList
     * @param serialNumbers
     * @param useLegacy Default value is true
     */
    public getDeviceConfigurationInconsistencyOptions(dataProvider: DataProvider, dataList: OpData[], serialNumbers: number[], useLegacy: boolean = true): JQueryPromise<any> {
        if (useLegacy) {
            return this.getDeviceConfigurationInconsistencyOptionsLegacy(dataProvider, dataList, serialNumbers)
        }

        return this.getDeviceConfigurationInconsistencyOptionsNew(dataProvider, dataList, serialNumbers)
    }

    /**
     * Use this method only if your control is after thin-client-removal refactor.
     * @param dataProvider
     * @param dataList
     * @param serialNumbers
     */
    private getDeviceConfigurationInconsistencyOptionsNew(dataProvider: DataProvider, dataList: OpData[], serialNumbers: number[]): JQueryPromise<TreeListOptions> {
        if (dataProvider == null || dataList == null || serialNumbers == null) {
            throw new Error("DataProvider, dataList and serialNumbers cannot be undefined.")
        }

        const deferer: JQueryDeferred<TreeListOptions> = $.Deferred<TreeListOptions>()
        let options: TreeListOptions = null

        if (dataList.length) {
            try {
                const treeDataSource: any[] = []
                let i = 1, parent = 1

                serialNumbers.forEach((serialNbr: number) => {
                    dataList.forEach((data: OpData, index: number) => {
                        if (index === 0) {
                            const headerData: OpData = new OpData(null)
                            headerData.serialNbr = serialNbr
                            headerData.indexNbr = 0;
                            headerData.idDataType = Enums.DataType.HELPER_SERIAL_NBR
                            headerData.dataType = dataProvider.DataType.getDataTypeLocal(headerData.idDataType)
                            headerData.value = serialNbr

                            headerData[DeviceService.TreeKeyFieldName] = i
                            headerData[DeviceService.TreeParentFieldName] = 0

                            treeDataSource.push(headerData)

                            parent = i++
                        }

                        if (serialNbr === data.serialNbr) {
                            const node: OpData = data
                            const dataType: OpDataType = dataProvider.DataType.getDataTypeLocal(data.idDataType)

                            node.dataType = dataType

                            node[DeviceService.TreeKeyFieldName] = i++
                            node[DeviceService.TreeParentFieldName] = parent

                            if (dataType.idDataTypeClass === Enums.DataTypeClass.Boolean) {
                                if (node.value == true) {
                                    node.value = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.True)
                                } else if (node.value == false) {
                                    node.value = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.False)
                                }
                            }

                            treeDataSource.push(node)
                        }
                    })
                })

                options = {
                    name: 'devicesInconsistencyTreeList',
                    type: GridTypes.NO_SEARCH_BAR_PAGINATED_SMALL,

                    buttonsOptions: {
                        enabled: false,
                    },

                    addCustomFilters: true,
                    columnsDictionary: this.getExtededParametersGridColumns(),
                    dataSource: new ArrayStore({
                        key: DeviceService.TreeKeyFieldName,
                        data: treeDataSource
                    }),

                    keyExpr: DeviceService.TreeKeyFieldName,
                    parentIdExpr: DeviceService.TreeParentFieldName
                }

                deferer.resolve(options)
            } catch (e) {
                ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                deferer.resolve(null)
            }
        } else {
            deferer.resolve(null)
        }

        return deferer.promise()
    }

    /**
     * This method is obsolete and kept for backward compatibility. Use getDeviceConfigurationInconsistencyOptionsNew if your control is after thin-client-removal refactor.
     * @param dataProvider
     * @param dataList
     * @param serialNumbers
     */
    private getDeviceConfigurationInconsistencyOptionsLegacy(dataProvider: DataProvider, dataList: OpData[], serialNumbers: number[]): JQueryPromise<any> {
        if (dataProvider == null || dataList == null || serialNumbers == null) {
            throw new Error("DataProvider, dataList and serialNumbers cannot be undefined.")
        }

        if (dataList.length) {
            const promises: JQueryPromise<any>[] = []
            const treeDataSource: any[] = []
            let i = 1, parent = 1

            serialNumbers.forEach((serialNbr) => {
                dataList.forEach((data, index) => {
                    if (index === 0) {
                        treeDataSource.push({
                            id: i,
                            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Device),
                            value: serialNbr,
                            idParent: 0,
                            serialNbr: serialNbr
                        })
                        parent = i++
                    }

                    if (serialNbr === data.serialNbr) {
                        const node: any = data
                        const nodePromise = dataProvider.DataType
                            .getDataType(data.idDataType)
                            .then((dataType: OpDataType) => {
                                if (dataType.descr) {
                                    node['caption'] = dataType.descr.toString()
                                }

                                if (dataType.unit) {
                                    node['unitName'] = dataType.unit.name
                                }

                                node['idParent'] = parent
                                node['id'] = i++

                                if (dataType.idDataTypeClass === Enums.DataTypeClass.Boolean) {
                                    if (node.value == true) node.value = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.True)
                                    if (node.value == false) node.value = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.False)
                                }

                                treeDataSource.push(node)
                            })

                        promises.push(nodePromise)
                    }
                })
            })

            return $.when(...promises)
                .then(() => {
                    return {
                        name: 'devicesInconsistencyTreeList',
                        type: GridTypes.NO_SEARCH_BAR_PAGINATED_SMALL,
                        parentIdExpr: 'idParent',
                        columns: [{
                            dataField: "caption",
                            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Description)
                        },
                        {
                            dataField: "index",
                            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Index)
                        },
                        {
                            dataField: "value",
                            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Value),
                            dataType: "string"
                        },
                        {
                            dataField: "unitName",
                            caption: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Unit),
                            dataType: "string"
                        }],
                        addCustomFilters: true,
                        dataSource: new DataSource(treeDataSource),
                        buttonsOptions: {
                            enabled: false
                        }
                    }
                })
        }

        return $.Deferred().resolve(null).promise()
    }

    public createDataSourceForExtendedParameters(dataProvider: DataProvider, device: OpDevice, allowedDataTypes: number[]): any {
        const items: any[] = []

        if (device && device.dataList && device.dataList.length > 0) {
            const filteredData: OpData[] = device.dataList.toArray().filter((dItem: OpData) => allowedDataTypes.some((idDataType: number) => dItem.idDataType == idDataType))

            let treeItemId: number = +new Date()

            for (let dItem of filteredData) {
                if (dItem.dataType == null) {
                    dItem.dataType = dataProvider.DataType.getDataTypeLocal(dItem.idDataType)
                }

                const treeItem: any = dItem
                treeItem[DeviceService.TreeKeyFieldName] = treeItemId++
                treeItem[DeviceService.TreeParentFieldName] = 0
                items.push(treeItem)
            }

            const grouping: _.Dictionary<OpData[]> = _.groupBy(filteredData, 'idDataType')

            if (grouping) {
                for (let groupKey in grouping) {
                    const groupedData: OpData[] = grouping[groupKey]
                    if (groupedData.length > 1) {

                        const sourceItem: OpData = groupedData[0]

                        const groupingRowItem: any = {
                            dataType: sourceItem.dataType,                             // dataType column
                            indexNbr: groupedData.map(row => row.indexNbr).join(';'),  // indexNbr column
                            unit: sourceItem.unit,                                     // unit column
                            rows: groupedData,                                         // child rows
                            typeId: sourceItem.dataType.idDataTypeClass,               // ???
                        }

                        groupingRowItem[DeviceService.TreeKeyFieldName] = sourceItem.idDataType
                        groupingRowItem[DeviceService.TreeParentFieldName] = 0

                        items.push(groupingRowItem)

                        groupedData.forEach((row: OpData) => {
                            row[DeviceService.TreeParentFieldName] = sourceItem.idDataType
                        })
                    }
                }

                items.push
            }
        }

        return new ArrayStore({
            key: DeviceService.TreeKeyFieldName,
            data: items
        })
    }

    // #endregion
}

export class DeviceServiceProvider implements ng.IServiceProvider {

    // #region IServiceProvider Implementation 

    public $get = ['$rootScope', function ($rootScope: ng.IRootScopeService) {
        return new DeviceService($rootScope)
    }]

    // #endregion
} 